/**
 * SegmentedButton — compound container that groups `<Segment>` cells
 * into a single visual control with one shared border and per-cell
 * dividers. Supports three behaviour modes:
 *
 *   - **radio**    — single-select (only one Segment active at a time).
 *   - **checkbox** — multi-select (any subset can be active).
 *   - **button**   — no managed state; each Segment is an independent
 *                    button whose `onClick` handler fires. Use for
 *                    toolbar-style groups (Bold / Italic / Underline).
 *
 * Figma source: "Segmented Button - Button - Checkbox - Radiobutton"
 * (node `3878:27095`). The Figma component combines all three roles
 * under one name, which is exactly the API surface we mirror here.
 *
 * Cells communicate with the group through React Context — see
 * `Segment.tsx`. Standalone `<Segment>` (outside a group) keeps the
 * same look but uses its own `selected` / `onClick` props directly.
 *
 * @format
 */

import {createContext, useMemo, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {surfaceVar} from 'ui/theme/surfaceContext';
import {
  useSurfaceContext,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';

import bordersTokens from 'ui/tokens/semantic/borders.json';
import {v} from 'ui/tokens/util';

const RADIUS_M_PX = parseInt(v(bordersTokens.radius.m));

export const SEGMENTED_SIZES = ['s', 'm', 'l'] as const;
export type SegmentedSize = (typeof SEGMENTED_SIZES)[number];

/**
 * Idle cell background tier. Switching this lets the SegmentedButton
 * stay legible on different host surfaces:
 *
 *   - `'surface-1'` — Figma's source variant. Use when the control
 *     sits on the page background (`surface.background`).
 *   - `'surface-2'` — one elevation higher. Default. Use when the
 *     control sits on a `surface-1` panel (FormCard etc.) — the most
 *     common case in our auth flows.
 *   - `'ghost'`     — transparent idle cells with a thin container
 *     outline. Use on arbitrary surfaces where you don't want a
 *     filled idle state. Active cell still uses the primary fill.
 */
export const SEGMENTED_SURFACES = ['surface-1', 'surface-2', 'ghost'] as const;
export type SegmentedSurface = (typeof SEGMENTED_SURFACES)[number];

// ── Context shared with child <Segment> cells ───────────────────────────

export interface SegmentedGroupContextValue {
  mode: 'radio' | 'checkbox' | 'button';
  size: SegmentedSize;
  surface: SegmentedSurface;
  disabled: boolean;
  /** True if this cell's value should render in the selected state. */
  resolveSelected: (value: string | undefined) => boolean;
  /** Called when the user clicks the cell. */
  handleClick: (value: string | undefined, ownOnClick?: () => void) => void;
}

export const SegmentedGroupContext = createContext<SegmentedGroupContextValue | null>(null);

// ── Public props (discriminated union — type-safe per mode) ─────────────

interface CommonGroupProps {
  /** Cells to render — a list of `<Segment>` elements. */
  children: ReactNode;
  /** Match the height/padding scale to a sibling control. Default `m`. */
  size?: SegmentedSize;
  /**
   * Idle cell background tier. Default `'surface-2'` — the typical
   * case when the control sits on a `surface-1` form card or panel.
   * Switch to `'surface-1'` on the page background, or `'ghost'` on
   * arbitrary surfaces.
   */
  surface?: SegmentedSurface;
  /** Surface-context override; inherits from `<SurfaceContextProvider>`. */
  context?: SurfaceContextValue;
  /** Disable the whole group. */
  disabled?: boolean;
}

interface RadioProps extends CommonGroupProps {
  mode?: 'radio';
  /** Currently-selected value. */
  value: string;
  /** Fires with the new value when the user picks a different cell. */
  onChange: (next: string) => void;
}

interface CheckboxProps extends CommonGroupProps {
  mode: 'checkbox';
  /** Currently-selected values. */
  value: string[];
  /** Fires with the updated array on toggle. */
  onChange: (next: string[]) => void;
}

interface ButtonProps extends CommonGroupProps {
  mode: 'button';
  /**
   * `button` mode has no managed state — each `<Segment>` carries its
   * own `onClick` and (optionally) `selected` prop. These two fields
   * are intentionally omitted from this branch of the union.
   */
}

export type SegmentedButtonProps = RadioProps | CheckboxProps | ButtonProps;

// ── Container chrome ───────────────────────────────────────────────────
// Per Figma (3878:27094): `content-stretch flex gap-px items-start
// overflow-clip relative rounded-[8px]`. The container is bare —
// the 1px gap reveals whatever sits behind the SegmentedButton.
//
// Ghost variant breaks that rule for a practical reason: with
// transparent cells the gap shows nothing either, so the group would
// be invisible without a border. Ghost adds a hairline outline.

const Row = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$ghost: boolean}>(({theme, $ghost}) => ({
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.25),
  borderRadius: RADIUS_M_PX,
  // No `overflow: hidden` here — clipping cell content broke the
  // IndicatorAnchor pattern (notification indicators at the cell
  // corners would get cut at the row boundary). Cells now round their
  // own outer corners via `:first-of-type` / `:last-of-type`, so an
  // anchor wrapping a cell can let an indicator stick out freely.
  // Keep the row at its content width when placed inside a flex parent.
  flexShrink: 0,
  ...($ghost && {
    border: `1px solid ${surfaceVar('onSurfaceMuted')}`,
  }),
}));

// ── Component ──────────────────────────────────────────────────────────

export function SegmentedButton(props: SegmentedButtonProps) {
  const {
    children,
    size = 'm',
    surface = 'surface-2',
    context: contextProp,
    disabled = false,
  } = props;
  const context = useSurfaceContext(contextProp);

  // Resolve the (selected, click) pair per mode and memoize so child
  // cells don't re-render on every container render.
  const ctxValue = useMemo<SegmentedGroupContextValue>(() => {
    if (props.mode === 'checkbox') {
      const {value, onChange} = props;
      return {
        mode: 'checkbox',
        size,
        surface,
        disabled,
        resolveSelected: v => (v == null ? false : value.includes(v)),
        handleClick: (v, ownOnClick) => {
          if (v != null) {
            const next = value.includes(v) ? value.filter(x => x !== v) : [...value, v];
            onChange(next);
          }
          ownOnClick?.();
        },
      };
    }
    if (props.mode === 'button') {
      return {
        mode: 'button',
        size,
        surface,
        disabled,
        // In button mode the group doesn't drive selection — each
        // cell's own `selected` prop wins (if any). Returning false
        // keeps the default to "not selected" so a Segment without an
        // explicit `selected` is just a plain button.
        resolveSelected: () => false,
        handleClick: (_v, ownOnClick) => {
          ownOnClick?.();
        },
      };
    }
    // radio (default)
    const {value, onChange} = props as RadioProps;
    return {
      mode: 'radio',
      size,
      surface,
      disabled,
      resolveSelected: v => v != null && v === value,
      handleClick: (v, ownOnClick) => {
        if (v != null && v !== value) onChange(v);
        ownOnClick?.();
      },
    };
  }, [props, size, surface, disabled]);

  // ARIA role: radiogroup for radio, group for checkbox / button.
  const role = props.mode === 'radio' || props.mode === undefined ? 'radiogroup' : 'group';

  return (
    <SegmentedGroupContext.Provider value={ctxValue}>
      <Row
        role={role}
        aria-disabled={disabled || undefined}
        data-color-scheme={context}
        $ghost={surface === 'ghost'}
      >
        {children}
      </Row>
    </SegmentedGroupContext.Provider>
  );
}
